import { moveToFulfilled, moveToPending, moveToRejected, remove, update } from '@ms/yammer-data-support';
import type { Reducer } from 'redux';

import type { FollowTopicAction, UnfollowTopicAction } from '../../domains/topicFollow/actions.types';
import type { TopicHeaderLoadAction } from '../../domains/topicHeaderLoad/actions.types';
import type { TopicCreateFulfilledAction } from '../../domains/topics/createActions.types';
import type { TopicEditAction } from '../../domains/topics/editActions.types';
import type { RemoveTopicFulfilledProps } from '../../domains/topics/removeTopicActions';
import type { RemoveTopicFulfilledAction } from '../../domains/topics/removeTopicActions.types';
import type { TypeaheadFulfilledAction } from '../../domains/typeahead/actions.types';
import type { Topic } from '../topics/types';

import type { TopicFollowings } from './types';

export type TopicFollowingAction =
  | FollowTopicAction
  | TopicHeaderLoadAction
  | UnfollowTopicAction
  | TopicEditAction
  | TopicCreateFulfilledAction
  | TypeaheadFulfilledAction
  | RemoveTopicFulfilledAction;

const initialState: TopicFollowings = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const followTopic = (state: TopicFollowings, topicId: Topic['id']) => {
  const topic = state.fulfilled.byId[topicId];

  if (!topic) {
    return state;
  }

  const followedTopic = {
    ...topic,
    viewerIsFollowing: true,
    followerCount: topic.followerCount + 1,
  };

  return update(state, topicId, followedTopic);
};

const unfollowTopic = (state: TopicFollowings, topicId: Topic['id']) => {
  const topic = state.fulfilled.byId[topicId];

  if (!topic) {
    return state;
  }

  const unfollowedTopic = {
    ...topic,
    viewerIsFollowing: false,
    followerCount: topic.followerCount - 1,
  };

  return update(state, topicId, unfollowedTopic);
};

const removeTopic = (state: TopicFollowings, topicIds: RemoveTopicFulfilledProps) => {
  const curatedTopicId = topicIds?.curatedTopicId;

  return curatedTopicId ? remove(state, [curatedTopicId]) : state;
};

const reducer: Reducer<TopicFollowings, TopicFollowingAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'TOPIC_HEADER.LOAD_PENDING':
      return moveToPending(state, [action.payload.topicId]);
    case 'TOPIC_HEADER.LOAD_REJECTED':
      return moveToRejected(state, [action.payload.topicId], action.payload.error);
    case 'TOPIC_HEADER.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicFollowings);
    case 'TOPIC.CREATE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicFollowings);
    case 'TYPEAHEAD.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicFollowings);
    case 'TOPIC.FOLLOW_FULFILLED':
      return followTopic(state, action.payload.topicId);
    case 'TOPIC.UNFOLLOW_FULFILLED':
      return unfollowTopic(state, action.payload.topicId);
    case 'TOPIC.FOLLOW_PENDING':
      return update(state, action.payload.topicId, { viewerIsFollowing: true });
    case 'TOPIC.UNFOLLOW_REJECTED':
      return update(state, action.payload.topicId, { viewerIsFollowing: true });
    case 'TOPIC.UNFOLLOW_PENDING':
      return update(state, action.payload.topicId, { viewerIsFollowing: false });
    case 'TOPIC.FOLLOW_REJECTED':
      return update(state, action.payload.topicId, { viewerIsFollowing: false });
    case 'TOPIC.REMOVE_FULFILLED':
      return removeTopic(state, action.payload);
    default:
      return state;
  }
};

export default reducer;
